import React, { useEffect, useState } from "react";

import { useSelector, useDispatch } from "react-redux";

import { Link, useHistory } from "react-router-dom";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel.jsx";
import { UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import TableReact, { SelectColumnFilter, multiSelectFilter, MultiSelectColumnFilter } from "../../components/table/Table";
import _ from "lodash";

import { comprobanteActions } from "../../store";
import saveExcel from "../../helper/ExportToXslx";
import { formatDate, formatMoney, getStatusComprobante } from "../../helper/formatter.js";

const columnsXls = [
    { header: "Fecha Emision", key: "fecha_emision", type: "date", format: "DD/MM/YYYY" },
    { header: "Serie", key: "talonario.serie" },
    { header: "Correlativo", key: "n_comprobante" },
    { header: "Registro", key: "tercero.registro" },
    { header: "Razon social", key: "tercero.razonsocial" },
    { header: "Ventas Excentas", key: "suma_venta_excenta" },
    { header: "Ventas no sujetas", key: "suma_venta_no_sujeta" },
    { header: "Ventas sujetas", key: "suma_venta_sujeta" },
    { header: "Importe", key: "importe" },
    { header: "Pendiente", key: "pendiente" },
];

let defaultFilterValues = {
    filters: [
        {
            id: "cancelado",
            value: ["pendiente pago"], //pendiente pago   pago parcial
        },
    ],
};

const ComprobantesEmitidos = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const comprobantes = useSelector((state) => state.comprobante.items);
    const terceros = useSelector((state) => state.tercero.items);

    const customComprobantes = React.useMemo(() => _(comprobantes).values().orderBy(["id"], ["desc"]).value(), [comprobantes]);

    const [selectedRows, setSelectedRows] = useState([]);
    const [rowsDrawn, setRowsDrawn] = useState([]);
    const [error, setError] = useState({});
    const [warn, setWarn] = useState({});

    const [canAbonar, setCanAbonar] = useState(false);
    const [canAnular, setCanAnular] = useState(false);

    const columns = React.useMemo(
        () => [
            {
                id: "comprobante",
                Header: "COMPROBANTE",
                accessor: (row) => row.talonario.serie + row.n_comprobante,
                canGroupBy: false,
                Cell: ({ row }) => {
                    return (
                        <Link
                            to={`/venta/comprobante/${row.original?.id}`}
                            className={"fw-bold text-nowrap " + (row.original?.anulado && "text-decoration-line-through")}>
                            {row.original?.talonario.serie + row.original?.n_comprobante}
                        </Link>
                    );
                },
            },
            {
                id: "razonsocial",
                Header: "RAZON SOCIAL",
                canGroupBy: false,
                accessor: (row) => terceros[row.terceroId]?.razonsocial,
                Cell: ({ row }) => (
                    <div className={"text-nowrap " + (row.original?.anulado && "text-decoration-line-through")}>
                        {row.values.razonsocial}
                    </div>
                ),
            },
            {
                Header: "IMPORTE",
                accessor: "importe",
                Cell: ({ row }) => (
                    <div className={"text-end text-nowrap " + (row.original?.anulado && "text-decoration-line-through")}>
                        {formatMoney(row.original?.importe)}
                    </div>
                ),
                canGroupBy: false,
            },
            {
                Header: "PENDIENTE",
                accessor: "pendiente",
                Cell: ({ row }) => (
                    <div className={"text-end text-nowrap " + (row.original?.anulado && "text-decoration-line-through")}>
                        {formatMoney(row.original?.pendiente <= 0 ? 0 : row.original?.pendiente)}
                    </div>
                ),
                canGroupBy: false,
            },
            {
                Header: "FECHA EMISION",
                id: "fecha_emision",
                accessor: (row) => formatDate(row.fecha_emision),
                canGroupBy: false,
                Cell: ({ row }) => (
                    <div className={"text-end text-nowrap " + (row.original?.anulado && "text-decoration-line-through")}>
                        {row.values.fecha_emision}
                    </div>
                ),
            },
            {
                Header: "AUTOR",
                accessor: "user.name",
                canGroupBy: false,
            },
            {
                Header: "ANULADO?",
                id: "anulado",
                accessor: (row) => (row.anulado ? "ANULADA" : "ACTIVA"),
                canGroupBy: false,
                Filter: SelectColumnFilter,
                Cell: ({ row }) => (
                    <>
                        {row.original?.anulado ? (
                            <span className="badge bg-red me-1 text-uppercase">ANULADA</span>
                        ) : (
                            <span className="badge bg-green me-1 text-uppercase">ACTIVA</span>
                        )}
                    </>
                ),
            },
            {
                Header: "ESTADO",
                id: "cancelado",
                accessor: (row) => (row.cancelado ? "pagada" : row.importe == row.pendiente ? "pendiente pago" : "pago parcial"),
                canGroupBy: false,
                Filter: MultiSelectColumnFilter,
                filter: multiSelectFilter,
                Cell: ({ row }) => getStatusComprobante(row.original),
            },
        ],
        [terceros]
    );

    const data = React.useMemo(() => customComprobantes, [comprobantes]);
    useEffect(() => {
        setCanAbonar(Object.keys(_.groupBy(selectedRows, "terceroId")).length == 1);
        setCanAnular(selectedRows.length > 0);
    }, [selectedRows]);

    const handleAbono = (e) => {
        e.preventDefault();
        if (_.filter(selectedRows, "anulado").length > 0) {
            setError({
                message: "Solo se puede abonar a comprobantes que no esten anulados",
            });
        } else {
            history.push({
                pathname: "/venta/registro_cobros/0",
                state: {
                    comprobantes: selectedRows,
                },
            });
        }
    };

    const handleAnular = (anular) => {
        if (anular) {
            setWarn({
                message: `Esta seguro que desea anular ${selectedRows.length} comprobantes`,
                anular: anular,
            });
        } else {
            setWarn({
                message: `Esta seguro que desea revertir anulacion a ${selectedRows.length} comprobantes`,
                anular: anular,
            });
        }
    };

    const handleAnularConfirmado = async (anular) => {
        const comprobantesIds = _.map(selectedRows, "id");
        const response = await fetch("/api/comprobante/anular", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ comprobantesIds: comprobantesIds, anular: anular }),
        });
        if (response.ok) {
            _.each(comprobantesIds, (id) => {
                let currentCmp = { ...comprobantes[id] };
                currentCmp.anulado = anular;
                dispatch(comprobanteActions.update(currentCmp));
            });
        } else {
            throw Error(await response.text());
        }
        setWarn({});
    };

    const buildExcel = ({ all = false, selection = false }) => {
        let fullData = [];
        if (all) {
            fullData = _.map(rowsDrawn, (comprobante) => {
                return { ...comprobante, ["tercero"]: terceros[comprobante.terceroId] || null };
            });
        } else if (selection) {
            fullData = _.map(selectedRows, (comprobante) => {
                return { ...comprobante, ["tercero"]: terceros[comprobante.terceroId] || null };
            });
        }

        saveExcel("Comprobantes", columnsXls, fullData);
    };

    return (
        <Panel>
            <PanelHeader>Listado de facturas emitidas</PanelHeader>

            {!_.isEmpty(error) ? (
                <SweetAlert
                    danger
                    confirmBtnText="Entendido!"
                    confirmBtnBsStyle="danger"
                    title="Error"
                    onConfirm={() => setError({})}>
                    {error.message}
                </SweetAlert>
            ) : !_.isEmpty(warn) ? (
                <SweetAlert
                    warning
                    showCancel
                    confirmBtnText="Continuar"
                    cancelBtnText="Cancelar"
                    confirmBtnBsStyle="warning"
                    cancelBtnBsStyle="default"
                    title={warn.anular ? "Anular comprobantes" : "Revertir anulacion de comprobantes"}
                    onConfirm={() => handleAnularConfirmado(warn.anular)}
                    onCancel={() => setWarn({})}>
                    {warn.message}
                </SweetAlert>
            ) : (
                <></>
            )}
            <PanelBody>
                <div className="row text-center">
                    <div>
                        <button type="button" className="btn btn-green me-1 mb-1" disabled={!canAbonar} onClick={handleAbono}>
                            Aplicar abono
                        </button>

                        <UncontrolledButtonDropdown direction="down" className="me-2 mb-1">
                            <button
                                type="button"
                                className="btn btn-danger "
                                disabled={!canAnular}
                                onClick={() => handleAnular(true)}>
                                Anular
                            </button>

                            <DropdownToggle color="danger" className="dropdown-toggle" disabled={!canAnular}>
                                <i className="fa fa-caret-down"></i>
                            </DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem onClick={() => handleAnular(true)}>Anular</DropdownItem>
                                <DropdownItem onClick={() => handleAnular(false)}>Revertir anulacion</DropdownItem>
                            </DropdownMenu>
                        </UncontrolledButtonDropdown>

                        <UncontrolledButtonDropdown direction="down" className="me-2 mb-1">
                            <button
                                type="button"
                                className="btn btn-indigo "
                                disabled={!rowsDrawn.length > 0}
                                onClick={() => buildExcel({ all: true })}>
                                <i className="fa fa-file-excel fa-fw"></i> Exportar (Excel)
                            </button>

                            <DropdownToggle color="indigo" className="dropdown-toggle" disabled={!rowsDrawn.length > 0}>
                                <i className="fa fa-caret-down"></i>
                            </DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem onClick={() => buildExcel({ all: true })}>Exportar contenido de tabla</DropdownItem>
                                <DropdownItem onClick={() => buildExcel({ selection: true })}>Exportar seleccion</DropdownItem>
                            </DropdownMenu>
                        </UncontrolledButtonDropdown>
                    </div>
                </div>
            </PanelBody>

            <TableReact
                columns={columns}
                data={data}
                selection={true}
                selectedListener={setSelectedRows}
                rowsDrawnListener={setRowsDrawn}
                // defaultFilterValues={defaultFilterValues}
            />
        </Panel>
    );
};

export default ComprobantesEmitidos;
